// Shared "select a patient" building block — used standalone at the top of
// the OT Charge Capture form, and again (its own independent instance) inside
// the embedded Lab & Rad Charges section, since that section carries its own
// patient context exactly as it did as a standalone page.
import React, { useState, useEffect } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ClearIcon from '@mui/icons-material/Clear';
import SectionCard from '../inventory/components/SectionCard';
import InvButton from '../inventory/components/InvButton';
import SelectPatientDialog from '../inventory/components/store/SelectPatientDialog';
import { getAllocatedAdmissions } from '../ipd/pages/patient-admission-ipd/api/registrationService';
import { IPD_COLORS } from '../inventory/theme';
import { OT_SECTION_SX, OT_SELECTED_GRADIENT } from './otTheme';

export function usePatientPicker() {
  const [admissions, setAdmissions] = useState([]);
  const [admissionsLoading, setAdmissionsLoading] = useState(false);
  const [admissionsError, setAdmissionsError] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [patient, setPatient] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setAdmissionsLoading(true);
    setAdmissionsError(null);
    // API: GET ipd_admission/?status=Allocated
    getAllocatedAdmissions()
      .then((list) => { if (!cancelled) setAdmissions(list); })
      .catch((e) => { if (!cancelled) { setAdmissions([]); setAdmissionsError(e.message || 'Failed to load allocated patients'); } })
      .finally(() => { if (!cancelled) setAdmissionsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return {
    admissions,
    admissionsLoading,
    admissionsError,
    searchOpen,
    openSearch: () => setSearchOpen(true),
    closeSearch: () => setSearchOpen(false),
    patient,
    selectPatient: (mapped) => { setPatient(mapped); setSearchOpen(false); },
    clearPatient: () => setPatient(null),
  };
}

export default function PatientPickerSection({ picker, title = 'Patient', sx }) {
  const {
    admissions, admissionsLoading, admissionsError,
    searchOpen, openSearch, closeSearch, patient, selectPatient, clearPatient,
  } = picker;

  return (
    <SectionCard
      title={title}
      sx={{ ...OT_SECTION_SX, ...sx }}
      actions={(
        <InvButton size="small" startIcon={<PersonSearchIcon />} onClick={openSearch}>
          {patient ? 'Change patient' : 'Search patient'}
        </InvButton>
      )}
    >
      {patient ? (
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1,
          px: 1.5, py: 1.25, border: `1px solid ${IPD_COLORS.selectedBorder}`, borderRadius: 1,
          background: OT_SELECTED_GRADIENT,
          boxShadow: IPD_COLORS.shadow.selected,
        }}>
          <Box>
            <Typography sx={{ color: IPD_COLORS.textPrimary, fontWeight: 600, fontSize: 14 }}>
              {patient.name || '—'}
            </Typography>
            <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary }}>
              UHID: {patient.uhid || '—'} · Gender: {patient.gender || '—'} · Age: {patient.age ? `${patient.age} yrs` : '—'}
              {patient.doctor_name ? ` · Doctor: ${patient.doctor_name}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" aria-label="Clear patient" onClick={clearPatient} sx={{ color: IPD_COLORS.textSecondary }}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ px: 1.5, py: 1.25, border: `1px dashed ${IPD_COLORS.border}`, borderRadius: 1 }}>
          <Typography sx={{ color: IPD_COLORS.textSecondary, fontSize: 14 }}>
            Search and select a patient to continue
          </Typography>
        </Box>
      )}

      <SelectPatientDialog
        open={searchOpen}
        onClose={closeSearch}
        onSelect={selectPatient}
        admissions={admissions}
        loading={admissionsLoading}
        error={admissionsError}
      />
    </SectionCard>
  );
}
